import type { SeoConfig } from "@/hooks/useSeo";

export interface Faq {
  q: string;
  a: string;
}

export interface Card {
  icon: string;
  title: string;
  text: string;
}

export interface Step {
  title: string;
  text: string;
  time: string;
}

export interface RelatedLink {
  href: string;
  label: string;
  desc: string;
}

export interface LandingContent {
  seo: SeoConfig;
  badge: string;
  h1a: string;
  h1gradient: string;
  h1b: string;
  sub: string;
  problemTitle: string;
  problemIntro: string;
  pains: Card[];
  servicesTitle: string;
  servicesIntro: string;
  services: Card[];
  /** Optional highlighted band (local presence, compliance note, offer detail…) */
  bandTitle: string;
  bandText: string;
  bandPoints: string[];
  processTitle: string;
  processIntro: string;
  steps: Step[];
  stats: { value: string; label: string }[];
  faqTitle: string;
  faqs: Faq[];
  relatedTitle: string;
  related: RelatedLink[];
  ctaTitle: string;
  ctaText: string;
}

export const CALENDLY = "https://calendly.com/alex-alcalde-aidealmedia-pyr_/30min";

export function serviceJsonLd(opts: { url: string; name: string; description: string; faqs: Faq[] }) {
  return [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: opts.name,
      provider: {
        "@type": "Organization",
        name: "AIdeal Media",
        url: "https://www.aidealmedia.info/",
      },
      url: opts.url,
      description: opts.description,
      areaServed: "United States",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: opts.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];
}

export const SHARED_STEPS: Step[] = [
  {
    title: "ROI Diagnostic",
    text: "We identify your top 3 high-leverage AI opportunities and build your strategic roadmap.",
    time: "1 week",
  },
  {
    title: "Design & Roadmap",
    text: "We architect your AI infrastructure and set contract-backed KPIs to ensure total alignment with your business goals.",
    time: "2 weeks",
  },
  {
    title: "Build & Training",
    text: "We deploy custom agents and train them on your proprietary data for seamless integration into your existing stack.",
    time: "4–6 weeks",
  },
  {
    title: "Continuous Optimization",
    text: "Measure. Optimize. Dominate. Monthly impact reports ensure your AI workforce scales alongside your revenue.",
    time: "Ongoing",
  },
];

export const SHARED_STATS = [
  { value: "3.8x", label: "Average 90-day ROI" },
  { value: "-42%", label: "Operational cost reduction" },
  { value: "+37%", label: "Conversion vs baseline" },
  { value: "-52%", label: "Support response time" },
];
